<template>
  <div>
    <breadcrumb-header :items="breadcrumbs" />

    <v-alert
      v-if="!isSupported"
      type="warning"
      class="mb-4"
      :text="t('passkey.not_supported')"
    />

    <template v-else>
      <v-card class="mb-4">
        <v-card-text>
          <v-list v-if="passkeys.length > 0">
            <v-list-item
              v-for="passkey in passkeys"
              :key="passkey.id"
              :subtitle="formatDate(passkey.created_at, 'LL')"
            >
              <template #title>
                <span v-if="editingId !== passkey.id">{{ passkey.name }}</span>
                <v-text-field
                  v-else
                  v-model="editingName"
                  density="compact"
                  hide-details
                  autofocus
                  class="mt-1"
                  @keyup="(e: KeyboardEvent) => { if (e.key === 'Enter') saveRename(passkey); else if (e.key === 'Escape') cancelRename(); }"
                />
              </template>
              <template #append>
                <template v-if="editingId !== passkey.id">
                  <v-btn
                    icon="fas fa-pencil"
                    variant="text"
                    size="small"
                    :title="t('passkey.rename')"
                    @click="startRename(passkey)"
                  />
                  <v-btn
                    icon="fas fa-trash"
                    variant="text"
                    size="small"
                    color="error"
                    :title="t('passkey.delete')"
                    @click="confirmDelete(passkey)"
                  />
                </template>
                <template v-else>
                  <v-btn
                    icon="fas fa-check"
                    variant="text"
                    size="small"
                    color="success"
                    :loading="savingRename"
                    @click="saveRename(passkey)"
                  />
                  <v-btn
                    icon="fas fa-xmark"
                    variant="text"
                    size="small"
                    @click="cancelRename"
                  />
                </template>
              </template>
            </v-list-item>
          </v-list>

          <v-alert
            v-else
            type="info"
            variant="tonal"
            :text="t('passkey.no_passkeys')"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            :loading="registering"
            prepend-icon="fas fa-key"
            @click="registerPasskey"
          >
            {{ t('passkey.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>

    <!-- Delete confirm dialog -->
    <v-dialog
      v-model="showDeleteDialog"
      max-width="400"
      persistent
    >
      <v-card :title="t('passkey.delete')">
        <v-card-text>{{ t('passkey.delete_confirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="deleting"
            @click="showDeleteDialog = false"
          >
            {{ t('actions.cancel') }}
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            @click="deletePasskey"
          >
            {{ t('passkey.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { startRegistration } from '@simplewebauthn/browser';
import axios from 'axios';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDate } from '@/composables';
import { useErrorStore } from '@/stores/errors.ts';

interface Passkey {
  id: number;
  name: string;
  created_at: string;
}

const { t } = useI18n();
const { showSnackMessage, handleError } = useErrorStore();
const { formatDate } = useDate();

const breadcrumbs = [
  {
    title: t('passkey.title', 2),
  },
];

const passkeys = ref<Passkey[]>([]);
const isSupported = ref(
  typeof window !== 'undefined'
  && !!window.PublicKeyCredential,
);
const registering = ref(false);
const deleting = ref(false);
const savingRename = ref(false);
const showDeleteDialog = ref(false);
const pendingDelete = ref<Passkey | null>(null);
const editingId = ref<number | null>(null);
const editingName = ref('');

async function fetchPasskeys(): Promise<void> {
  try {
    const { data } = await axios.get<Passkey[]>('/api/passkeys');
    passkeys.value = data;
  }
  catch (e) {
    handleError(e, t('errors.failed_generic'));
  }
}

async function registerPasskey(): Promise<void> {
  if (!isSupported.value) {
    return;
  }

  registering.value = true;
  try {
    const { data: options } = await axios.post('/passkeys/register-options');
    const registration = await startRegistration({ optionsJSON: options });

    await axios.post('/passkeys/register', {
      name: generateDefaultName(),
      passkey: JSON.stringify(registration),
    });

    showSnackMessage(t('passkey.register_success'), 'success');
    await fetchPasskeys();
  }
  catch (e: any) {
    if (e?.name === 'NotAllowedError') {
      return;
    }
    handleError(e, t('passkey.register_error'));
  }
  finally {
    registering.value = false;
  }
}

function generateDefaultName(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) {
    return 'iPhone';
  }
  if (/iPad/.test(ua)) {
    return 'iPad';
  }
  if (/Android/.test(ua)) {
    return 'Android';
  }
  if (/Mac/.test(ua)) {
    return 'Mac';
  }
  if (/Windows/.test(ua)) {
    return 'Windows PC';
  }
  return 'Passkey';
}

function confirmDelete(passkey: Passkey): void {
  pendingDelete.value = passkey;
  showDeleteDialog.value = true;
}

async function deletePasskey(): Promise<void> {
  if (!pendingDelete.value) {
    return;
  }
  deleting.value = true;
  try {
    await axios.delete(`/api/passkeys/${pendingDelete.value.id}`);
    showSnackMessage(t('passkey.delete_success'), 'success');
    await fetchPasskeys();
    showDeleteDialog.value = false;
  }
  catch (e) {
    handleError(e, t('errors.failed_generic'));
  }
  finally {
    deleting.value = false;
    pendingDelete.value = null;
  }
}

function startRename(passkey: Passkey): void {
  editingId.value = passkey.id;
  editingName.value = passkey.name;
}

function cancelRename(): void {
  editingId.value = null;
  editingName.value = '';
}

async function saveRename(passkey: Passkey): Promise<void> {
  if (!editingName.value.trim()) {
    return;
  }
  savingRename.value = true;
  try {
    await axios.patch(`/api/passkeys/${passkey.id}`, {
      name: editingName.value.trim(),
    });
    showSnackMessage(t('passkey.rename_success'), 'success');
    await fetchPasskeys();
    cancelRename();
  }
  catch (e) {
    handleError(e, t('errors.failed_generic'));
  }
  finally {
    savingRename.value = false;
  }
}

onMounted(fetchPasskeys);
</script>
